import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { restoreSession } from "./api/client";
import { PlatformApp } from "./PlatformApp";
import { AppShell } from "./components/AppShell";
import { Appointments } from "./screens/Appointments";
import { Attendance } from "./screens/Attendance";
import { BatchRecall } from "./screens/BatchRecall";
import { Calendar } from "./screens/Calendar";
import { Escalations } from "./screens/Escalations";
import { Chart } from "./screens/Chart";
import { Dashboard } from "./screens/Dashboard";
import { ClinicPoster } from "./screens/ClinicPoster";
import { Organisation } from "./screens/Organisation";
import { HowItWorks } from "./screens/HowItWorks";
import { PatientSearch } from "./screens/PatientSearch";
import { PrintableDocument } from "./screens/PrintableDocument";
import { Queue } from "./screens/Queue";
import { Recalls } from "./screens/Recalls";
import { ResultsWaiting } from "./screens/ResultsWaiting";
import { AcceptInvitation } from "./screens/AcceptInvitation";
import { SignIn } from "./screens/SignIn";
import { useSession } from "./useSession";

/**
 * The clinic portal: sign in, find a patient, read a chart.
 *
 * The guard here is a convenience, never a control. It keeps a signed-out
 * person off a screen that would only show errors. Every piece of patient data
 * on those screens is refused by the server, not by this component
 * (CLAUDE.md safety rule 1).
 *
 * The first thing it does is try to bring back a session, because the access
 * token is held in memory and a page reload empties it. Without that step a
 * doctor who reloads would have to find their phone and type a code again.
 */
export function App() {
  return (
    <Routes>
      {/* The Healtrics console is a different surface with a different token
          audience, so it is split off before the clinic portal's guard and
          its session restore run at all (ADR-024). */}
      <Route path="/platform/*" element={<PlatformApp />} />
      {/* Open, because whoever holds the link has no account yet. It sits
          outside the clinic portal's guard for the same reason /signin does. */}
      <Route path="/invite" element={<AcceptInvitation />} />
      <Route path="/*" element={<ClinicPortal />} />
    </Routes>
  );
}

function ClinicPortal() {
  const { signedIn, clinic } = useSession();
  const [restoring, setRestoring] = useState(true);

  useEffect(() => {
    void restoreSession().finally(() => setRestoring(false));
  }, []);

  if (restoring) {
    return <p className="empty centred">Signing you back in…</p>;
  }

  if (!signedIn || !clinic) {
    return (
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    );
  }

  return (
    <AppShell>
      <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/patients" element={<PatientSearch />} />
          <Route path="/patients/:id" element={<Chart />} />
          <Route path="/documents/:kind/:id" element={<PrintableDocument />} />
          <Route path="/results" element={<ResultsWaiting />} />
          <Route path="/recalls" element={<Recalls />} />
          <Route path="/queue" element={<Queue />} />
          <Route path="/messages" element={<Escalations />} />
          <Route path="/appointments" element={<Appointments />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/attendance" element={<Attendance />} />
          <Route path="/batches" element={<BatchRecall />} />
          <Route path="/help" element={<HowItWorks />} />
          <Route path="/poster" element={<ClinicPoster />} />
          <Route path="/organisation" element={<Organisation />} />
          <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
